import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type IssueFlowReport } from '@apm/contracts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { issueApi, issueKeys } from '../api';
import { displayDate } from '../../../lib/date';

/**
 * Năm biểu đồ vận hành — APM-ISSUE-j, docs/plan-issue-management.md §7.
 *
 * ## ⚠️ Không có `asOf`, và không có `AsOfField` cạnh nhóm này
 *
 * Luật #7 ngoại lệ 2: `from`/`to` của chính nhóm *là* bộ điều khiển thời gian.
 * Thêm `asOf` bên cạnh là hai trục thời gian trên một màn hình mà không ai giải
 * thích được cái nào thắng — đúng bài học đã ghi ở `team-performance-table`.
 *
 * Riêng "tuổi việc đang mở" nhìn về **hiện tại**, nên nó dán nhãn mốc chụp
 * riêng (`agingAsOf`) thay vì mượn nhãn kỳ của bốn cái kia.
 *
 * ## Một lượt gọi cho cả năm
 *
 * Cả năm đọc cùng một tập issue trong cùng một khoảng. Năm query là năm lần
 * quét cùng dữ liệu cho một màn hình, và chúng sẽ lệch nhau khi có ai đó ghi
 * nhận một vấn đề mới ở giữa.
 */
export function IssueReports({
  projectId,
  range,
}: {
  projectId: string;
  range: { from?: string; to?: string };
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: issueKeys.flow(projectId, range),
    queryFn: () => issueApi.flow(projectId, range),
  });

  const state = {
    loading: report.isPending,
    error: report.isError ? errorMessage(report.error, t) : null,
  };

  return (
    <>
      <ArrivalChart data={report.data} {...state} />
      <div className="grid gap-4 xl:grid-cols-2">
        <CycleTable data={report.data} {...state} />
        <SlaTable data={report.data} {...state} />
        <AgingChart data={report.data} {...state} />
        <MaintenanceTable data={report.data} {...state} />
      </div>
    </>
  );
}

interface PartProps {
  data: IssueFlowReport | undefined;
  loading: boolean;
  error: string | null;
}

function ArrivalChart({ data, loading, error }: PartProps): ReactNode {
  const { t } = useTranslation();
  const points = (data?.arrival ?? []).map((bucket) => ({
    week: bucket.week.slice(5),
    created: bucket.created,
    closed: bucket.closed,
  }));

  return (
    <ChartFrame
      title={t('issueTracking.reports.arrival.title')}
      help="issueArrival"
      subtitle={t('issueTracking.reports.arrival.subtitle')}
      loading={loading}
      error={error}
      empty={points.length === 0 ? { title: t('issueTracking.reports.noData') } : null}
    >
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={points}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="week" fontSize={12} />
          <YAxis allowDecimals={false} fontSize={12} />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="created"
            name={t('issueTracking.reports.arrival.created')}
            stroke={FLOW_VAR.todo}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="closed"
            name={t('issueTracking.reports.arrival.closed')}
            stroke={FLOW_VAR.done}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/**
 * Phân bố thời gian xử lý — **bảng phân vị, không phải đường trung bình**.
 *
 * Kanban Guide định nghĩa SLE là một khoảng thời gian kèm một xác suất. Phân bố
 * thời gian xử lý ticket có đuôi dài rất nặng, nên một con số trung bình luôn
 * nằm ở chỗ không có ticket nào — và nó che đúng những cái làm khách gọi điện.
 */
function CycleTable({ data, loading, error }: PartProps): ReactNode {
  const { t } = useTranslation();
  const rows = data?.cycle ?? [];

  return (
    <ChartFrame
      title={t('issueTracking.reports.cycle.title')}
      help="issueCycle"
      subtitle={t('issueTracking.reports.cycle.subtitle')}
      loading={loading}
      error={error}
      empty={
        rows.every((row) => row.samples === 0)
          ? { title: t('issueTracking.reports.noClosed') }
          : null
      }
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-1">{t('issueTracking.column.priority')}</th>
            <th className="py-1">{t('issueTracking.reports.cycle.samples')}</th>
            <th className="py-1">{t('issueTracking.reports.cycle.p50')}</th>
            <th className="py-1">{t('issueTracking.reports.cycle.p85')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.priority} className="border-t border-border">
              <td className="py-1">{t(`issueTracking.priority.${row.priority}`)}</td>
              <td className="py-1">{row.samples}</td>
              <td className="py-1">{hours(row.p50Hours, t)}</td>
              <td className="py-1 font-medium">{hours(row.p85Hours, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartFrame>
  );
}

function SlaTable({ data, loading, error }: PartProps): ReactNode {
  const { t } = useTranslation();
  const rows = data?.sla ?? [];

  return (
    <ChartFrame
      title={t('issueTracking.reports.sla.title')}
      help="issueSla"
      subtitle={t('issueTracking.reports.sla.subtitle')}
      loading={loading}
      error={error}
      empty={
        rows.every((row) => row.responseSamples === 0 && row.resolutionSamples === 0)
          ? { title: t('issueTracking.reports.noClosed') }
          : null
      }
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-1">{t('issueTracking.column.priority')}</th>
            <th className="py-1">{t('issueTracking.reports.sla.response')}</th>
            <th className="py-1">{t('issueTracking.reports.sla.resolution')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.priority} className="border-t border-border">
              <td className="py-1">{t(`issueTracking.priority.${row.priority}`)}</td>
              <td className="py-1">{percent(row.responseMetPct, row.responseSamples, t)}</td>
              <td className="py-1">{percent(row.resolutionMetPct, row.resolutionSamples, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartFrame>
  );
}

function AgingChart({ data, loading, error }: PartProps): ReactNode {
  const { t } = useTranslation();
  const rows = (data?.aging ?? []).map((bucket) => ({
    label:
      bucket.upToDays === null
        ? t('issueTracking.reports.aging.older')
        : t('issueTracking.reports.aging.upTo', { days: bucket.upToDays }),
    count: bucket.count,
    overdue: bucket.overdue,
  }));

  return (
    <ChartFrame
      title={t('issueTracking.reports.aging.title')}
      /* Mốc chụp **riêng**: bốn biểu đồ kia kể lại một khoảng, cái này chụp hiện tại. */
      subtitle={t('issueTracking.reports.aging.subtitle', {
        at: data ? displayDate(data.agingAsOf) : '',
      })}
      help="issueAging"
      loading={loading}
      error={error}
      empty={
        rows.every((row) => row.count === 0) ? { title: t('issueTracking.reports.noOpen') } : null
      }
    >
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" fontSize={12} />
          <YAxis allowDecimals={false} fontSize={12} />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="count"
            name={t('issueTracking.reports.aging.count')}
            fill={FLOW_VAR.inProgress}
          />
          <Bar
            dataKey="overdue"
            name={t('issueTracking.reports.aging.overdue')}
            fill={RAG_VAR.red}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

function MaintenanceTable({ data, loading, error }: PartProps): ReactNode {
  const { t } = useTranslation();
  const rows = data?.maintenance ?? [];
  const total = rows.reduce((sum, row) => sum + row.hours, 0);

  return (
    <ChartFrame
      title={t('issueTracking.reports.maintenance.title')}
      help="issueMaintenance"
      subtitle={t('issueTracking.reports.maintenance.subtitle')}
      loading={loading}
      error={error}
      empty={total === 0 ? { title: t('issueTracking.reports.noHours') } : null}
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-1">{t('issueTracking.reports.maintenance.category')}</th>
            <th className="py-1">{t('issueTracking.reports.maintenance.count')}</th>
            <th className="py-1">{t('issueTracking.reports.maintenance.hours')}</th>
            <th className="py-1">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.category ?? 'none'} className="border-t border-border">
              <td className="py-1">
                {row.category === null
                  ? t('issueTracking.reports.maintenance.unclassified')
                  : t(`issueTracking.maintenanceCategory.${row.category}`)}
              </td>
              <td className="py-1">{row.count}</td>
              <td className="py-1">{row.hours}</td>
              <td className="py-1">
                {total === 0 ? '—' : `${Math.round((row.hours / total) * 100)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartFrame>
  );
}

/** `null` = chưa đóng việc nào ở mức này. Hiện một gạch, không hiện `0` — "xong
    tức thì" và "chưa có mẫu" là hai câu khác hẳn nhau. */
function hours(value: number | null, t: (key: string) => string): string {
  return value === null ? '—' : `${value}${t('issueTracking.reports.hourSuffix')}`;
}

/** Tương tự: không có cam kết thì gạch, không phải 0%. */
function percent(value: number | null, samples: number, t: (key: string) => string): string {
  if (value === null) return samples === 0 ? '—' : t('issueTracking.noCommitment');
  return `${value}%`;
}
